import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, setJwt } from '../api/client.js';
import { AnimatedBackground } from '../components/AnimatedBackground.jsx';
import { OminiaLogo } from '../components/OminiaLogo.jsx';
import { useTheme } from '../theme/ThemeContext.jsx';

export function Login() {
  const { scheme, toggle } = useTheme();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const qp = params.get('email');
    if (qp) setEmail(decodeURIComponent(qp));
    if (params.get('trial') === 'expirado') setError('');
  }, [params]);

  const welcomeCadastro = params.get('novo') === '1';
  const trialFimMsg = params.get('trial') === 'expirado';

  async function onSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await api('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
        skipAuth: true,
      });
      setJwt(data.token);
      nav('/app/dashboard');
    } catch (err) {
      if (err?.status === 402 && err?.data?.code === 'TRIAL_EXPIRED') {
        setError(String(err.message || '') + ' Fale com vendas ou ative o plano Ouro para continuar.');
      } else {
        setError(err.message || 'Não foi possível entrar. Confira e-mail e senha.');
      }
    } finally {
      setLoading(false);
    }
  }

  const fieldCls =
    'mt-2 w-full rounded-xl border px-4 py-3 text-[15px] transition placeholder:text-zinc-400 outline-none bg-white border-zinc-200 text-zinc-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 dark:bg-night-surface dark:border-zinc-600 dark:text-zinc-100 dark:placeholder:text-zinc-500 dark:focus:border-emerald-400 dark:focus:ring-emerald-500/15';

  return (
    <div className="relative min-h-screen overflow-x-hidden px-4 py-10 sm:py-14">
      <AnimatedBackground />
      <div className="relative z-10 mx-auto w-full max-w-lg">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <Link to="/" className="inline-flex flex-col gap-2 sm:gap-3">
            <OminiaLogo size={64} />
            <span className="font-display text-lg font-bold sm:text-xl">
              <span className="text-gradient">Omin</span>
              <span className="text-gradient-ia">IA</span>
            </span>
          </Link>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={toggle}
              className="rounded-full border border-zinc-300/90 bg-white/70 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-zinc-700 backdrop-blur-sm transition hover:bg-white dark:border-zinc-600 dark:bg-zinc-900/80 dark:text-zinc-200 dark:hover:bg-zinc-800"
            >
              {scheme === 'dark' ? 'Claro' : 'Escuro'}
            </button>
            <Link
              className="rounded-full border border-emerald-700/25 bg-emerald-500/10 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-800 transition hover:bg-emerald-500/20 dark:border-emerald-400/30 dark:bg-emerald-500/10 dark:text-emerald-200 dark:hover:bg-emerald-500/20"
              to="/cadastro"
            >
              Criar conta
            </Link>
          </div>
        </div>

        <div
          className="rounded-[1.75rem] border border-zinc-200/90 bg-white/95 p-7 shadow-[0_24px_80px_rgba(15,23,42,0.12)] backdrop-blur-xl sm:p-9
          dark:border-zinc-700/70 dark:bg-zinc-950/95 dark:shadow-[0_28px_90px_rgba(0,0,0,0.55),0_0_0_1px_rgba(255,255,255,0.06)]"
        >
          <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">Entrar</h1>
          <p className="mt-3 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
            Acesso ao painel da sua loja. Ainda sem conta?{' '}
            <Link
              className="font-semibold text-emerald-700 underline-offset-4 hover:underline dark:text-emerald-300"
              to="/cadastro"
            >
              Criar conta
            </Link>{' '}
            — teste grátis ou plano Ouro.
          </p>

          {welcomeCadastro ? (
            <div className="mt-4 rounded-xl border border-emerald-500/35 bg-emerald-500/10 px-4 py-3 text-xs font-semibold text-emerald-900 dark:border-emerald-400/35 dark:bg-emerald-500/15 dark:text-emerald-50">
              Conta criada com sucesso. Entre para configurar sua empresa e publicar a loja na web.
            </div>
          ) : null}
          {trialFimMsg ? (
            <div className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-xs font-semibold text-amber-950 dark:border-amber-400/35 dark:bg-amber-500/15 dark:text-amber-50">
              O período de teste terminou. Assine o plano Ouro ou fale com vendas para voltar ao painel.
            </div>
          ) : null}

          <form className="mt-8 space-y-5" onSubmit={onSubmit}>
            <label className="block text-xs font-bold uppercase tracking-wide text-zinc-700 dark:text-zinc-300">
              E-mail
              <input
                className={fieldCls}
                type="email"
                autoComplete="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <label className="block text-xs font-bold uppercase tracking-wide text-zinc-700 dark:text-zinc-300">
              Senha
              <input
                className={fieldCls}
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>
            {error ? (
              <p
                className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800 dark:border-red-900/50 dark:bg-red-950/50 dark:text-red-200"
                role="alert"
              >
                {error}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-white shadow-lg shadow-emerald-900/25 transition enabled:hover:brightness-105 enabled:active:scale-[0.99] disabled:opacity-55 dark:shadow-emerald-950/40"
            >
              {loading ? 'A entrar…' : 'Entrar'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
